import type { MediaKind } from "@/core/model/source.ts";

/**
 * Browsers report an empty or wrong MIME type for several containers (mkv, avi, heic),
 * so the extension is a real fallback rather than a convenience.
 */
const EXTENSIONS: { readonly [K in MediaKind]: readonly string[] } = {
  image: ["png", "jpg", "jpeg", "webp", "avif", "gif", "bmp", "tif", "tiff", "heic", "heif"],
  audio: ["wav", "mp3", "m4a", "aac", "flac", "ogg", "oga", "opus", "weba"],
  video: ["mp4", "m4v", "mov", "webm", "mkv", "avi", "flv", "wmv", "ts", "3gp", "mpg", "mpeg"],
};

export const ACCEPT_ATTRIBUTE = Object.entries(EXTENSIONS)
  .flatMap(([, extensions]) => extensions.map((extension) => `.${extension}`))
  .concat(["image/*", "audio/*", "video/*"])
  .join(",");

export function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  return dot === -1 ? "" : fileName.slice(dot + 1).toLowerCase();
}

export function detectMediaKind(file: File): MediaKind | null {
  const [type] = file.type.split("/");
  if (type === "image" || type === "audio" || type === "video") return type;

  const extension = extensionOf(file.name);
  for (const kind of ["image", "audio", "video"] as const) {
    if (EXTENSIONS[kind].includes(extension)) return kind;
  }
  return null;
}

export function baseName(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  return dot <= 0 ? fileName : fileName.slice(0, dot);
}
